import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {
  getParentCardCollapseStatus,
  removeItem,
  selectKP11CentersNAppVList,
  selectKP11SelectCartCentersNApps,
  updateParentCollapseStatus,
} from '../../../../app-store/kp11CartSlice'
import {fetchPlanIcon} from '../../../../utils/fetch-plan-icon'
import {kpCloseBtn} from '../../../../assets'
import KP11LinkManagePlanIncentiveButtonWithModal from './kp-11Link-manage-plan-incentives'
import KP11LinkPromotions from '../kp-11Link-utility/kp11-promotions'
import {KP11OrderSummaryStyle} from './kp-11Link-order-summary-styles'
import KP11LinkGenerateLinkButtonWithModal from '../kp-11Link-generateLink/kp-11Link-generateLink'
import {SchedulerViewComponent} from '../../../keap-scheduler-v2/scheduler-view'
import {useTransitionDataDetails} from '../kp-11Link-utility/kp11-link-use-data'
import {
  countryCurrencyEnum,
  formatMeetingDate,
  onboardingFeeCode,
} from '../kp-11Link-utility/kp11-utility'
import {Icon} from '@thryvlabs/maverick'
import {
  NON_REMOVABLE_PRODUCT_LIST,
  KEAP_ADDONS_PARENT_CODES,
} from '../../../../utils/constants'
import {useAuth0} from '@auth0/auth0-react'
import {getUserRoles} from '../../../../utils/get-user-roles'
import {KPPromotionsStyle} from '../../kp-shared-link/kp-sharedLink-order-summary/kp-sharedLink-order-summary-styles'
import {
  selectedLinkType,
  setInfluencerName,
} from '../../../../app-store/kpGlobalStateSlice'
import {
  selectBundleId,
  selectEffectiveRole,
} from '../../../../app-store/selector'
import {formatPrice} from '../../../../utils/currency-format'

const NON_WORKFORCE_KICKOFF_CODES = [
  'seobase-5-thryv-m2m',
  'tl-mc-5-thryv-m2m',
  'addon-socialadsbronze-5-m2m',
  'addon-socialadsgold-5-m2m',
  'addon-socialadssilver-5-m2m',
  'addon-6-mc-socialcontentgen-m2m',
]

const isWorkforceItem = item => {
  const code = item?.code || ''
  const name = item?.name?.toLowerCase?.() || ''
  return code.startsWith('wc_') || name.includes('workforce center')
}

const getFlattenedCartItems = ({centers = [], apps = [], oneTimeFees = []}) =>
  [...centers, ...apps, ...oneTimeFees]
    .filter(Boolean)
    .flatMap(item => [item, ...(item?.addons || [])])

const getOnboardingFeeItem = ({oneTimeFees = [], selectedCountry}) => {
  const onboardingFee = oneTimeFees.find(fee => fee?.code === onboardingFeeCode)
  if (!onboardingFee) return null

  const selectedCurrency = countryCurrencyEnum[selectedCountry] || 'USD'
  const unitAmount =
    onboardingFee?.currencies?.find(c => c.currency === selectedCurrency)
      ?.unitAmount ??
    onboardingFee?.unitAmount ??
    onboardingFee?.amount ??
    0

  return {
    ...onboardingFee,
    quantity: onboardingFee?.quantity || 1,
    unitAmount,
    amount: unitAmount,
  }
}

const isWorkforceOnlyKickoffCart = ({
  centers = [],
  apps = [],
  oneTimeFees = [],
}) => {
  const hasWorkforceCenter = [...centers, ...apps].some(isWorkforceItem)
  const hasNonWorkforceKickoffItem = getFlattenedCartItems({
    centers,
    apps,
    oneTimeFees,
  }).some(item => NON_WORKFORCE_KICKOFF_CODES.includes(item?.code))

  return hasWorkforceCenter && !hasNonWorkforceKickoffItem
}

const getEffectiveCartForWorkforceKickoff = ({
  cartCentersAndApps,
  centersNAppVList,
  scheduleKickOffCallDetails,
  selectedCountry,
}) => {
  if (!cartCentersAndApps) return cartCentersAndApps

  const cartCenters = cartCentersAndApps?.centers || []
  const cartApps = cartCentersAndApps?.apps || []
  const cartOneTimeFees = cartCentersAndApps?.oneTimeFees || []

  const isKickoffScheduled = !!scheduleKickOffCallDetails?.isCallScheduled
  const isKickoffSkipped = !!scheduleKickOffCallDetails?.callDetails?.skipped
  const isOnlyWorkforceForKickoff = isWorkforceOnlyKickoffCart({
    centers: cartCenters,
    apps: cartApps,
    oneTimeFees: cartOneTimeFees,
  })
  const hasWorkforceCenter = [...cartCenters, ...cartApps].some(isWorkforceItem)

  // If kickoff is skipped for Workforce-only scenario, remove onboarding fee.
  if (isKickoffSkipped && isOnlyWorkforceForKickoff) {
    return {
      ...cartCentersAndApps,
      oneTimeFees: cartOneTimeFees.filter(
        fee => fee?.code !== onboardingFeeCode,
      ),
    }
  }

  // Auto-apply onboarding fee when Workforce is present and kickoff is scheduled.
  if (!hasWorkforceCenter || !isKickoffScheduled) {
    return cartCentersAndApps
  }

  const isOnboardingFeeAlreadyInCart = cartOneTimeFees.some(
    fee => fee?.code === onboardingFeeCode,
  )
  if (isOnboardingFeeAlreadyInCart) {
    return cartCentersAndApps
  }

  const onboardingFeeForCart = getOnboardingFeeItem({
    oneTimeFees: centersNAppVList?.oneTimeFees || [],
    selectedCountry,
  })
  if (!onboardingFeeForCart) {
    return cartCentersAndApps
  }

  return {
    ...cartCentersAndApps,
    oneTimeFees: [...cartOneTimeFees, onboardingFeeForCart],
  }
}

export const KP11LinkOrderSummaryComponent = ({
  selectedCountry = 'US',
  keapPartnerDetails,
  accountInfoWithSub,
  thryvAccount,
  cartCentersAndApps: propsCartCentersAndApps, // Keep for backward compatibility
  defaultCartCentersNApps,
  allCoupons,
  removeCoupon,
  plansIncentiveGroups,
  togglePlanIncentive,
  fetchAndApplyPromoCode,
  keapPartnerId,
}) => {
  const {user} = useAuth0()
  const [influencerNote, setInfluencerNote] = useState('')
  const userRoles = getUserRoles(user)
  const effectiveRole = useSelector(selectEffectiveRole)
  const isInfluencerOps =
    (Array.isArray(effectiveRole) && effectiveRole.includes('InfluencerOps')) ||
    userRoles.includes('InfluencerOps')
  const bundleId = useSelector(selectBundleId)
  const linkType = useSelector(selectedLinkType)
  // Get cart data from Redux
  const reduxCartCentersAndApps = useSelector(selectKP11SelectCartCentersNApps)
  const parentCardCollapseStatus = useSelector(getParentCardCollapseStatus)
  const centersNAppVList = useSelector(selectKP11CentersNAppVList)
  const cartCentersAndApps = useMemo(() => {
    return reduxCartCentersAndApps?.centers?.length > 0 ||
      reduxCartCentersAndApps?.apps?.length > 0 ||
      reduxCartCentersAndApps?.oneTimeFees?.length > 0
      ? reduxCartCentersAndApps
      : {}
  }, [reduxCartCentersAndApps])
  const dispatch = useDispatch()

  const [showScheduleModal, setShowScheduleModalStatus] = useState(false)
  const [scheduleKickOffCallDetails, setScheduledKickOffCallDetails] =
    useState(null)
  const [scheduleCreativeCallDetails, setScheduledCreativeCallDetails] =
    useState(null)

  const effectiveCartCentersAndApps = useMemo(() => {
    return getEffectiveCartForWorkforceKickoff({
      cartCentersAndApps,
      centersNAppVList,
      scheduleKickOffCallDetails,
      selectedCountry,
    })
  }, [
    cartCentersAndApps,
    centersNAppVList,
    scheduleKickOffCallDetails,
    selectedCountry,
  ])

  useEffect(() => {
    if (!scheduleKickOffCallDetails?.callDetails?.skipped) {
      return
    }

    const isSkipStillAllowed = isWorkforceOnlyKickoffCart(cartCentersAndApps)
    if (!isSkipStillAllowed) {
      setScheduledKickOffCallDetails(null)
    }
  }, [cartCentersAndApps, scheduleKickOffCallDetails])

  const {transitionData} =
    // const {transitionData, isKickOffCallExist, isCreativeCallExist} =
    useTransitionDataDetails({
      // alreadyAddedCartCentersNApps,
      defaultCartCentersNApps,
      cartCentersAndApps: effectiveCartCentersAndApps,
      accountInfoWithSub,
      keapPartnerDetails,
      allCoupons,
      scheduleKickOffCallDetails,
      scheduleCreativeCallDetails,
      keapPartnerId,
    })

  const getPrice = (item, currency = 'USD') => {
    // if (item?.currencies) {
    //   const priceObj = item?.currencies.find(c => c.currency === currency)
    //   return priceObj ? priceObj.unitAmount : 0
    // }

    if (item?.amount != null) {
      // if (item?.unitAmount != null) {
      // Convert to string only if not already
      let amount = item.amount
      // if (item.name === 'SEO') {
      //   amount = item?.customFields?.alphaAmount - item.amount
      //   amount = amount > 0 ? amount : 0
      // } else if (item?.name?.toLowerCase().includes('seo')) {
      //   amount = amount - item.subtotal
      // }
      const amountStr = String(amount)

      // Strip any non-numeric characters except . and -
      const parsed = parseFloat(amountStr.replace(/[^0-9.-]+/g, ''))

      return isNaN(parsed) ? 0 : parsed
    } else if (item?.unitAmount != null) {
      // if (item?.unitAmount != null) {
      // Convert to string only if not already
      const amountStr = String(item.unitAmount)

      // Strip any non-numeric characters except . and -
      const parsed = parseFloat(amountStr.replace(/[^0-9.-]+/g, ''))

      return isNaN(parsed) ? 0 : parsed
    }
    return 0
  }

  //  Calculate total for all sections - centers, apps, onetimefees
  const calculateCartTotal = (cartCentersAndApps, currency = 'USD') => {
    let total = 0

    const addSectionTotal = section => {
      if (!Array.isArray(section)) return

      section.forEach(plan => {
        const isAlreadyAdded = plan.isAlreadyAdded ?? plan.isAlreadyAdded
        const isUpgraded = plan.isUpgraded ?? plan.isUpgraded
        // let isSEOPlan = plan?.name?.toLowerCase().includes('seo')
        // if (!isAlreadyAdded || isSEOPlan) {
        if (!isAlreadyAdded || isUpgraded) {
          // const multiplier =
          //   plan.customFields?.alphaAmount?.amount ??
          //   plan.customFields?.budget?.amount ??
          //   1
          // total += getPrice(plan, currency) * multiplier
          total += getPrice(plan, currency)
        }

        if (
          Array.isArray(plan.addons) &&
          ![
            'seobase-5-thryv-m2m',
            'addon-5-keap-addons',
            'appmarket-5-thryv-m2m',
          ].includes(plan.code)
        ) {
          plan.addons.forEach(addon => {
            const isAlreadyAdded = addon.isAlreadyAdded ?? addon.isAlreadyAdded
            const isUpgraded = addon.isUpgraded ?? addon.isUpgraded

            if (!isAlreadyAdded || isUpgraded) {
              // const multiplier =
              //   addon.customFields?.alphaAmount?.amount ??
              //   addon.customFields?.budget?.amount ??
              //   1
              // total += getPrice(addon, currency) * multiplier
              total += getPrice(addon, currency)
            }
          })
        }
      })
    }

    addSectionTotal(cartCentersAndApps?.apps || [])
    addSectionTotal(cartCentersAndApps?.centers || [])
    addSectionTotal(cartCentersAndApps?.oneTimeFees || [])

    return formatPrice(total ?? 0, 1, '', selectedCountry)
  }

  const currency = countryCurrencyEnum[selectedCountry]
  const totalAmount = calculateCartTotal(effectiveCartCentersAndApps, currency)

  const handleRemoveItem = () => {
    Object.keys(parentCardCollapseStatus).forEach(key => {
      dispatch(
        updateParentCollapseStatus({
          groupKey: key,
          collapse: true,
        }),
      )
    })
  }
  // --- helper functions ---
  const PlanItem = ({
    name,
    code,
    groupKey,
    amount,
    isAlreadyAdded,
    isUpgradeItem,
    icon,
    customFields,
    handleRemoveItem,
  }) => {
    return (
      <div className="cart-plan-item">
        <div className="item-header-text-wrap">
          {icon && (
            <div className="cart-plan-item-icon">
              <img
                src={icon}
                alt="plan logo"
                className={`options-logo rounded ${
                  name === 'Google Business Profile Optimization'
                    ? 'img-resize'
                    : ''
                }`}
              />
            </div>
          )}
          <div className="cart-plan-item-title">{name}</div>
        </div>

        <div className="d-flex cart-plan-item-amount align-items-center gap-1">
          {/* {isPlanOverriden
            ? '$' + overridenAmount
            : isAlreadyAdded && !isUpgradeItem
            ? 'Existing'
            : `$${(amount ?? 0).toFixed(2)}`} */}
          {isAlreadyAdded && !isUpgradeItem
            ? 'Existing'
            : `${formatPrice(amount ?? 0, 1, '', selectedCountry)}`}
          {!isAlreadyAdded &&
            !NON_REMOVABLE_PRODUCT_LIST.find(item => item === code) && (
              <button
                onClick={() => {
                  handleRemoveItem()
                  dispatch(removeItem({groupKey, itemCode: code}))
                }}
              >
                <Icon
                  type={'regular'}
                  variant={`x`}
                  height={'15'}
                  width={'15'}
                  color={'#FF6100'}
                />
              </button>
            )}
        </div>
      </div>
    )
  }

  // --- main section renderer ---
  const renderCartSection = (sectionName, plans = []) => {
    if (!plans.length) return null
    const excludedAddonsToRender = [
      'addon-5-signatures_usage',
      'app-websitebuilder',
    ]

    const rankItem = item => {
      if (
        ![
          'addon-socialadsgold-5-m2m',
          'addon-socialadssilver-5-m2m',
          'addon-socialadsbronze-5-m2m',
        ].includes(item.code)
      )
        return 1
      else if (item.code === 'addon-socialadsbronze-5-m2m') return 2
      else if (item.code === 'addon-socialadssilver-5-m2m') return 3
      else if (item.code === 'addon-socialadsgold-5-m2m') return 4
    }

    const itemComparator = (a, b) => {
      const rankA = rankItem(a)
      const rankB = rankItem(b)
      return rankA - rankB
    }

    return (
      <>
        {plans.map(plan => {
          const icon = fetchPlanIcon(plan.name)?.icon
          return (
            <div key={`${plan.id}-${plan.name}`} className="cart-item-wrap">
              <PlanItem
                name={plan.name}
                amount={plan.amount}
                groupKey={plan.groupKey}
                // currencies={plan.currencies}
                code={plan.code}
                isAlreadyAdded={plan.isAlreadyAdded}
                isUpgradeItem={plan.isUpgraded}
                icon={icon}
                customFields={plan.customFields}
                handleRemoveItem={handleRemoveItem}
                selectedCountry={selectedCountry}
              />

              {plan.addons?.length > 0 &&
                ![
                  ...KEAP_ADDONS_PARENT_CODES,
                  'appmarket-5-thryv-m2m',
                  'seobase-5-thryv-m2m',
                ].includes(plan.code) && (
                  <div className="cart-addons-wrap">
                    {plan.addons
                      .filter(
                        addon => !excludedAddonsToRender.includes(addon.code),
                      )
                      .sort(itemComparator)
                      .map(addon => (
                        <PlanItem
                          key={`${addon.id}-${addon.name}`}
                          name={addon.name}
                          code={addon.code}
                          groupKey={plan.groupKey}
                          amount={addon.amount}
                          // currencies={addon.currencies}
                          isAlreadyAdded={addon.isAlreadyAdded}
                          isUpgradeItem={addon.isUpgraded}
                          icon={fetchPlanIcon(addon.name)?.icon}
                          selectedCountry={selectedCountry}
                        />
                      ))}
                  </div>
                )}
            </div>
          )
        })}
      </>
    )
  }

  // function fetchAccountInformation() {
  //   const {firstName, lastName, email, company} = thryvAccount.accountInfo || {}
  //   const {
  //     street1: address,
  //     city,
  //     country,
  //     region: state,
  //     phone,
  //     postalCode,
  //   } = thryvAccount.accountInfo.businessAddress || {}
  //   const data = {
  //     firstName,
  //     lastName,
  //     email,
  //     phone,
  //     company,
  //     address,
  //     city,
  //     country,
  //     state,
  //     postalCode,
  //   }
  //   setAccountInformation(data)
  // }

  // const checkApptAllowStatus = () => {
  //   const {
  //     hasSeoOrTlInAlreadyAddedCart,
  //     hasSeoOrTlInStructuredCart,
  //     hasThryvDesg2AlreadyAddedCart,
  //     hasThryvDesg2StructuredCart,
  //   } = cartChecksForAppointments(alreadyAddedCartCentersNApps, structuredCart)

  //   const isKickOffApptAllow =
  //     hasSeoOrTlInAlreadyAddedCart ||
  //     (!hasSeoOrTlInAlreadyAddedCart && hasSeoOrTlInStructuredCart)
  //   const isCreativeApptAllow =
  //     hasThryvDesg2AlreadyAddedCart ||
  //     (!hasThryvDesg2AlreadyAddedCart && hasThryvDesg2StructuredCart)

  //   return {isKickOffApptAllow, isCreativeApptAllow}
  // }
  const checkGenerateLinkBtnStatus = () => {
    if (isInfluencerOps) {
      if (influencerNote.length === 0 || totalAmount === '$0.00') {
        return true
      } else {
        return false
      }
    }
    const hasNewItem = transitionData?.anyNewItemInCartCentersAndApps
    const hasUpgradedItem = transitionData?.anyUpgradedItemInCartCentersAndApps

    const hasKickoffItem = !!transitionData?.isKickOffApptHasSeoOrTlInCart
    const isKickoffScheduled =
      !!transitionData?.kickOffCallDetails?.isKickOffCallScheduled

    // WFC alone should not require a kickoff call — only SEO/TL/Social Ads do
    const isWfcOnlyKickoff = isWorkforceOnlyKickoffCart({
      centers: cartCentersAndApps?.centers || [],
      apps: cartCentersAndApps?.apps || [],
      oneTimeFees: cartCentersAndApps?.oneTimeFees || [],
    })
    const effectiveKickoffRequired = hasKickoffItem && !isWfcOnlyKickoff

    const hasCreativeItem = !!transitionData?.isCreativeApptHasThryvDesg2InCart
    const isCreativeScheduled =
      !!transitionData?.creativeCallDetails?.isCreativeCallScheduled

    const missingReasons = []

    // Disable if no new item
    if (!hasNewItem) {
      missingReasons.push('No new item added to cart')
    }

    // Disable if kickoff item exists but kickoff not scheduled (WFC-only carts bypass this)
    if (effectiveKickoffRequired && !isKickoffScheduled) {
      missingReasons.push(
        'Kickoff call not scheduled for SEO / Leads / Social Ads item',
      )
    }

    // Disable if creative item exists but creative not scheduled
    if (hasCreativeItem && !isCreativeScheduled) {
      missingReasons.push('Creative call not scheduled for Thryv Design item')
    }

    // Button enabled only if:
    // has new item AND
    // (no effective kickoff requirement OR kickoff scheduled) AND
    // (no creative item OR creative scheduled)
    const isButtonEnabled =
      (hasNewItem || hasUpgradedItem) &&
      (!effectiveKickoffRequired || isKickoffScheduled) &&
      (!hasCreativeItem || isCreativeScheduled)

    const isButtonDisabled = !isButtonEnabled

    if (isButtonDisabled) {
      console.warn(
        'Generate Link button disabled due to:',
        missingReasons.join(', '),
      )
    }

    return isButtonDisabled
  }

  const checkAppointmentButtonStatus = () => {
    const hasKickoffAppt = !!transitionData?.isKickOffApptHasSeoOrTlInCart
    const hasCreativeAppt = !!transitionData?.isCreativeApptHasThryvDesg2InCart

    // Use OR here — disable if EITHER is missing
    const isButtonDisabled = !hasKickoffAppt && !hasCreativeAppt

    const missingReasons = []

    if (!hasKickoffAppt) {
      missingReasons.push(
        'Kickoff appointment requirement missing (no SEO / Leads / Social Ads item in cart)',
      )
    }

    if (!hasCreativeAppt) {
      missingReasons.push(
        'Creative appointment requirement missing (no Thryv Design item in cart)',
      )
    }

    if (isButtonDisabled) {
      console.warn(
        'Appointment button disabled due to:',
        missingReasons.join(', '),
      )
    }

    return isButtonDisabled
  }

  return (
    <KP11OrderSummaryStyle>
      <div className="cart-wrapper">
        <h3 className="cart-header">Order Summary</h3>
        <div className="cart-body">
          <div className="cart-items-wrap">
            <div className="cart-items">
              <div className="item-title">Software</div>
              {renderCartSection(
                'Centers',
                effectiveCartCentersAndApps.centers,
              )}
            </div>
            <div className="cart-items">
              <div className="item-title">Add-Ons</div>
              {renderCartSection('Apps', effectiveCartCentersAndApps.apps)}
            </div>
            <div className="cart-items">
              <div className="item-title">One Time Fees</div>
              {renderCartSection(
                'Onetime Fees',
                effectiveCartCentersAndApps.oneTimeFees,
              )}
            </div>
            <div className="cart-items">
              <div className="item-title">Promotions</div>
              <KP11LinkPromotions
                allCoupons={allCoupons}
                thryvAccount={thryvAccount}
                cartCentersAndApps={effectiveCartCentersAndApps}
                fetchAndApplyPromoCode={fetchAndApplyPromoCode}
                removeCoupon={removeCoupon}
              ></KP11LinkPromotions>
            </div>
            <div className="cart-items">
              <div className="order-coupons">
                {Object.values(allCoupons)
                  .flat()
                  .map(c => (
                    <div key={`${c.code}-${c.name}`} className="coupon-item">
                      <div className="coupon-item-name">
                        {c.name || c.description || c.code}
                      </div>
                      <div
                        className="coupon-item-btn"
                        onClick={() => removeCoupon(c.code)}
                      >
                        <img src={kpCloseBtn} alt="close button" />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
            {isInfluencerOps && (
              <div className="cart-items">
                <div className="item-title">
                  Influencer <span style={{color: 'red'}}>*</span>
                </div>
                <KPPromotionsStyle>
                  <div className="search-field-wrap">
                    <input
                      disabled={totalAmount === '$0.00'}
                      id="influencer-note"
                      type="text"
                      value={influencerNote}
                      onChange={e => {
                        setInfluencerNote(e.target.value)
                        dispatch(setInfluencerName(e.target.value))
                      }}
                      placeholder="Influencer Name"
                      className="search-input"
                      required
                    />
                  </div>
                </KPPromotionsStyle>
              </div>
            )}
            {bundleId && (
              <div className="cart-items">
                <div className="item-title">Share Link Id</div>
                <KPPromotionsStyle>
                  <div className="search-field-wrap">
                    <input
                      id="slp-bundle-id"
                      type="text"
                      value={bundleId}
                      readOnly
                      disabled
                      className="search-input"
                    />
                  </div>
                </KPPromotionsStyle>
              </div>
            )}
          </div>
          <div
            className={`cart-total  ${
              isInfluencerOps || bundleId ? 'no-border-top' : ''
            }`}
          >
            <div className="total-body">
              <div className="total-title">Total</div>
              <div className="total-amount">
                {totalAmount.toLocaleString('en-US', {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 2,
                })}
                *
              </div>
            </div>
            <div className="total-subtext">
              {selectedCountry === 'AU'
                ? `*All Plans include GST except SEO`
                : `*Excludes taxes, fees, and incentives`}
            </div>
          </div>

          <div className="cart-action-btn-wrap">
            {transitionData?.kickOffCallDetails?.isKickOffCallScheduled && (
              <div className="cart-meeting-dt">
                <div>SEO / Leads / Social Ads Kickoff Call</div>
                <div>
                  {scheduleKickOffCallDetails?.callDetails?.skipped
                    ? 'SKIPPED'
                    : formatMeetingDate(
                        transitionData?.kickOffCallDetails?.kickOffCallSchedule,
                      )}
                </div>
              </div>
            )}
            {transitionData?.creativeCallDetails?.isCreativeCallScheduled && (
              <div className="cart-meeting-dt">
                <div>Website Design Call</div>
                <div>
                  {scheduleCreativeCallDetails?.callDetails?.skipped
                    ? 'SKIPPED'
                    : formatMeetingDate(
                        transitionData?.creativeCallDetails
                          ?.creativeCallSchedule,
                      )}
                </div>
              </div>
            )}
            <div className="cart-action-btn">
              {linkType === '1:1' && !isInfluencerOps && (
                <button
                  onClick={() => {
                    setShowScheduleModalStatus(prev => !prev)
                  }}
                  className="generate-link-btn"
                  disabled={checkAppointmentButtonStatus()}
                >
                  {transitionData?.creativeCallDetails
                    ?.isCreativeCallScheduled ||
                  transitionData?.kickOffCallDetails?.isKickOffCallScheduled
                    ? 'Change'
                    : 'Select'}{' '}
                  Appointment Time
                </button>
              )}
              {linkType === '1:many' ? (
                <KP11LinkGenerateLinkButtonWithModal
                  structuredCart={effectiveCartCentersAndApps}
                  influencerName={''}
                  keapPartnerDetails={{}}
                  selectedCountry={selectedCountry}
                  keapPartnerId={keapPartnerId}
                  promotions={allCoupons}
                  plansIncentive={plansIncentiveGroups}
                  defaultObFeeCoupon={{}}
                  transitionData={transitionData}
                  isGenrateLBtnDisabled={checkGenerateLinkBtnStatus()}
                />
              ) : (
                <KP11LinkGenerateLinkButtonWithModal
                  cartCentersAndApps={effectiveCartCentersAndApps}
                  transitionData={transitionData}
                  isGenrateLBtnDisabled={checkGenerateLinkBtnStatus()}
                  keapPartnerId={keapPartnerId}
                  isInfluencerOps={isInfluencerOps}
                  // scheduleKickOffCallDetails={scheduleKickOffCallDetails}
                  // scheduleCreativeCallDetails={scheduleCreativeCallDetails}
                ></KP11LinkGenerateLinkButtonWithModal>
              )}

              <KP11LinkManagePlanIncentiveButtonWithModal
                plansIncentiveGroups={plansIncentiveGroups}
                togglePlanIncentive={togglePlanIncentive}
              ></KP11LinkManagePlanIncentiveButtonWithModal>
            </div>
          </div>
        </div>
      </div>
      <SchedulerViewComponent
        accountInformation={transitionData?.accountInformation}
        transitionData={transitionData}
        structuredCart={effectiveCartCentersAndApps}
        showScheduleModal={showScheduleModal}
        setShowScheduleModalStatus={setShowScheduleModalStatus}
        isCreativeCallExist={transitionData?.isCreativeApptHasThryvDesg2InCart}
        isKickOffCallExist={transitionData?.isKickOffApptHasSeoOrTlInCart}
        scheduleKickOffCallDetails={scheduleKickOffCallDetails}
        onSchedulerComplete={() => {
          setScheduledKickOffCallDetails({
            isCallScheduled: true,
            callDetails: {skipped: true},
          })
        }}
        setScheduledKickOffCallDetails={setScheduledKickOffCallDetails}
        scheduleCreativeCallDetails={scheduleCreativeCallDetails}
        setScheduledCreativeCallDetails={setScheduledCreativeCallDetails}
        selectedCountry={selectedCountry}
      />
    </KP11OrderSummaryStyle>
  )
}
